import { useEffect, useRef } from 'react';

import type { Interaction } from '@/types';

/**
 * Auto-opens the credential-requests drawer on the first `none → pending`
 * transition (and on mount when a request is already pending), instead of
 * relying on the passive toast + "Needs input" header chip that the operator
 * must click. Credential requests carry a HARD ~10-minute backend timeout, so a
 * missed transient toast means the request expires unanswered — for a blocking,
 * deadline-bound input, auto-open is the correct behavior.
 *
 * Mirrors {@link useInteractionToast}'s one-shot pattern: a `seenRef` Set keyed
 * on `interaction.key` fires the auto-open exactly once per distinct request.
 * Interaction keys (`#interaction#{conv}#{req}`) are unique and never reused, so
 * the set needs no pruning. Manual close only flips the drawer's `open` state and
 * never clears the set, so a request auto-opened once and then dismissed while
 * still pending is never re-opened (we don't fight the user).
 *
 * Selection: opens to the FIRST-unseen pending request. `pendingInteractions` is
 * sorted ascending by timestamp (see `useInteractions`), so that is the OLDEST
 * pending request — the one with the least of its 10-minute budget remaining,
 * i.e. the most urgent to surface first.
 *
 * While the drawer is already open, a newly-arriving sibling request is marked
 * seen but does NOT change the selection — surfacing it in-place (roster row +
 * count badge + toast) instead of yanking the operator off a form they are
 * actively filling. This is a deliberate, accepted tradeoff (ENG-3976 follow-up).
 *
 * @param pendingInteractions all pending requests across the conversation tree
 * @param isDrawerOpen        whether the credential drawer is currently open
 * @param onAutoOpen          opens the credential drawer to a specific request
 */
export function useAutoOpenCredentialDrawer(
  pendingInteractions: Interaction[],
  isDrawerOpen: boolean,
  onAutoOpen: (requestKey: string) => void
) {
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Drawer already visible: any new request is already surfaced in-place
    // (roster row + count badge + toast). Mark all current pending keys seen so
    // a later close→reopen never yanks the user off the request they were on,
    // and never re-opens a key they've already seen. Do NOT change selection.
    if (isDrawerOpen) {
      for (const interaction of pendingInteractions) {
        seenRef.current.add(interaction.key);
      }
      return;
    }

    // Drawer closed: auto-open to the first-unseen (== oldest, the array is
    // timestamp-ascending) pending request. Fires on the none→pending edge, on
    // mount-with-pending, and for a genuinely new request that arrives after a
    // prior drawer close.
    const next = pendingInteractions.find(
      interaction => !seenRef.current.has(interaction.key)
    );
    if (!next) return;

    // Mark the whole current batch seen (not just `next`) so auto-open fires at
    // most once per batch — a sibling already pending in the same set never
    // triggers a second yank; the toast remains its backstop.
    for (const interaction of pendingInteractions) {
      seenRef.current.add(interaction.key);
    }
    onAutoOpen(next.key);
  }, [pendingInteractions, isDrawerOpen, onAutoOpen]);
}
